import { z } from 'zod';
import { isoTimestampSchema } from './primitives.js';
/**
 * Directory (LDAP / Active Directory) sign-in, configured by the administrator.
 *
 * Standards only: the server speaks LDAPv3 (RFC 4511) through a maintained
 * client library, builds every filter with RFC 4515 escaping, authenticates
 * with an LDAP simple bind as the user's own DN, and protects the connection
 * with LDAPS or StartTLS (RFC 4513). Nothing here is a custom protocol.
 *
 * ADMIN ONLY. Unlike `SystemSettings` this is never served publicly, and the
 * service account's password is write-only: it is stored encrypted and no
 * response ever carries it back - only whether one is set.
 */
/** How the connection to the directory is protected. */
export const ldapSecuritySchema = z.enum([
    /** TLS from the first byte, `ldaps://` (usually port 636). */
    'ldaps',
    /** Plain `ldap://` upgraded with the StartTLS extended operation before any bind. */
    'starttls',
    /** No encryption. Passwords cross the network in clear text - for lab use only. */
    'none',
]);
/** `ldap://host[:port]` or `ldaps://host[:port]` - no path, query or credentials. */
export const ldapUrlSchema = z
    .string()
    .trim()
    .max(512)
    .regex(/^ldaps?:\/\/(\[[0-9A-Fa-f:.]+\]|[A-Za-z0-9](?:[A-Za-z0-9.-]{0,252}))(:\d{1,5})?\/?$/, 'Use ldap://host[:port] or ldaps://host[:port]');
/** An attribute description: a name such as `sAMAccountName`, or a numeric OID. */
export const ldapAttributeSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^([A-Za-z][A-Za-z0-9-]*|\d+(\.\d+)+)$/, 'Not a valid attribute name');
const ldapConfigShape = {
    enabled: z.boolean(),
    /** What the sign-in page calls it: "Sign in with your Contoso account". */
    name: z.string().trim().min(1).max(60),
    /**
     * Directory servers, tried in order. The next one is used only when one
     * cannot be reached - a wrong password is never retried elsewhere.
     */
    urls: z.array(ldapUrlSchema).max(10),
    security: ldapSecuritySchema,
    /** Check the server's certificate. Turn off only to diagnose; never in production. */
    verifyCertificate: z.boolean(),
    /** PEM certificate(s) of the CA that issued the servers' certificates. Empty uses the system's. */
    caCertificate: z.string().max(32_000),
    /** The service account that searches the directory. Empty binds anonymously. */
    bindDn: z.string().trim().max(1024),
    /** Where users are searched for, e.g. `DC=corp,DC=example,DC=com`. */
    baseDn: z.string().trim().max(1024),
    /**
     * RFC 4515 filter every user must match, e.g. enabled person accounts. It
     * carries no placeholder: what someone types is matched separately, against
     * `loginAttributes`, and never spliced into a filter string.
     */
    userFilter: z
        .string()
        .trim()
        .min(1)
        .max(2048)
        .refine((value) => !value.includes('%s') && !value.includes('{0}'), {
        message: 'No placeholder here: list only what every user must match, e.g. (objectClass=user). The name people type is matched by "Sign in with" (sAMAccountName).',
    }),
    /** What a person may type to sign in, matched exactly - `sAMAccountName`, `userPrincipalName`, `mail`. */
    loginAttributes: z.array(ldapAttributeSchema).min(1).max(5),
    /** Never changes for an account, so renames and moves keep the same Orbit Sync user. */
    uniqueIdAttribute: ldapAttributeSchema,
    emailAttribute: ldapAttributeSchema,
    displayNameAttribute: ldapAttributeSchema,
    /** When set, only members of this group may sign in (and be imported). */
    requiredGroupDn: z.string().trim().max(1024),
    /**
     * Count membership through nested groups too, using Active Directory's
     * LDAP_MATCHING_RULE_IN_CHAIN. Other directories only understand direct
     * membership - turn this off for them.
     */
    nestedGroups: z.boolean(),
    /** Create an Orbit Sync account the first time a directory user signs in. */
    autoCreateUsers: z.boolean(),
    /**
     * Accounts created from the directory (at sign-in or by a sync) start
     * disabled, and an administrator enables each one before it can sign in.
     */
    newUsersDisabled: z.boolean(),
    /** Members of this group may sign in to the admin panel with full rights. */
    adminGroupDn: z.string().trim().max(1024),
    /** Members of this group may sign in to the admin panel as operators. */
    operatorGroupDn: z.string().trim().max(1024),
    /**
     * Attach a directory user to an existing LOCAL account with the same email,
     * keeping its conversations. From then on it signs in through the directory.
     */
    linkExistingAccounts: z.boolean(),
    /**
     * How often every directory user is imported, so colleagues can find each
     * other before their first sign-in. 0 syncs only when asked.
     */
    syncIntervalMinutes: z.number().int().min(0).max(10_080),
};
const ldapConfigObject = z.object(ldapConfigShape);
/** Cross-field rules, shared by the stored shape and the update body. */
function checkConsistency(config, ctx) {
    if (config.enabled && config.urls.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['urls'], message: 'Add at least one server' });
    }
    if (config.enabled && config.baseDn.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['baseDn'], message: 'The search base is required' });
    }
    const wantsLdaps = config.security === 'ldaps';
    config.urls.forEach((url, index) => {
        if (url.toLowerCase().startsWith('ldaps://') !== wantsLdaps) {
            ctx.addIssue({
                code: 'custom',
                path: ['urls', index],
                message: wantsLdaps
                    ? 'LDAPS needs an ldaps:// address'
                    : 'StartTLS and unencrypted connections need an ldap:// address',
            });
        }
    });
}
export const ldapConfigSchema = ldapConfigObject.superRefine(checkConsistency);
/**
 * Body of `PUT /api/v1/admin/ldap` - the whole configuration.
 *
 * `bindPassword` is write-only: omit it to keep the stored one, send a string
 * to replace it, or `null` to remove it.
 */
export const updateLdapConfigInputSchema = z
    .strictObject({
    ...ldapConfigShape,
    bindPassword: z.string().max(1024).nullable().optional(),
})
    .superRefine(checkConsistency);
/**
 * Sensible starting values for Active Directory. `userFilter` keeps disabled
 * accounts out (bit 2 of `userAccountControl`, tested with the bitwise-AND
 * matching rule), so a person disabled in AD cannot sign in here either.
 */
export const DEFAULT_LDAP_CONFIG = {
    enabled: false,
    name: 'Active Directory',
    urls: [],
    security: 'ldaps',
    verifyCertificate: true,
    caCertificate: '',
    bindDn: '',
    baseDn: '',
    userFilter: '(&(objectCategory=person)(objectClass=user)(!(userAccountControl:1.2.840.113556.1.4.803:=2)))',
    loginAttributes: ['sAMAccountName', 'userPrincipalName', 'mail'],
    uniqueIdAttribute: 'objectGUID',
    emailAttribute: 'mail',
    displayNameAttribute: 'displayName',
    requiredGroupDn: '',
    nestedGroups: true,
    autoCreateUsers: true,
    newUsersDisabled: true,
    adminGroupDn: '',
    operatorGroupDn: '',
    linkExistingAccounts: true,
    syncIntervalMinutes: 60,
};
/** Values that suit OpenLDAP and most other RFC 4519 / inetOrgPerson directories. */
export const OPENLDAP_PRESET = {
    userFilter: '(objectClass=inetOrgPerson)',
    loginAttributes: ['uid', 'mail'],
    uniqueIdAttribute: 'entryUUID',
    emailAttribute: 'mail',
    displayNameAttribute: 'cn',
    nestedGroups: false,
};
export const ACTIVE_DIRECTORY_PRESET = {
    userFilter: DEFAULT_LDAP_CONFIG.userFilter,
    loginAttributes: DEFAULT_LDAP_CONFIG.loginAttributes,
    uniqueIdAttribute: DEFAULT_LDAP_CONFIG.uniqueIdAttribute,
    emailAttribute: DEFAULT_LDAP_CONFIG.emailAttribute,
    displayNameAttribute: DEFAULT_LDAP_CONFIG.displayNameAttribute,
    nestedGroups: true,
};
/**
 * Stored configuration made whole: a missing or invalid key falls back to its
 * default, as `resolveSettings` does, so one bad value cannot take the rest
 * down. A combination that breaks the cross-field rules leaves sign-in OFF.
 */
export function resolveLdapConfig(raw) {
    const source = typeof raw === 'object' && raw !== null ? raw : {};
    const resolved = {};
    for (const key of Object.keys(ldapConfigShape)) {
        const parsed = ldapConfigShape[key].safeParse(source[key]);
        resolved[key] = parsed.success ? parsed.data : DEFAULT_LDAP_CONFIG[key];
    }
    const checked = ldapConfigSchema.safeParse(resolved);
    return checked.success ? checked.data : { ...resolved, enabled: false };
}
/**
 * Body of `POST /api/v1/admin/ldap/test`: a configuration to try before saving
 * it, and optionally one user's credentials to try signing in with. The
 * password is used for one bind and forgotten.
 */
export const ldapTestInputSchema = z.object({
    config: updateLdapConfigInputSchema,
    testLogin: z
        .object({
        username: z.string().trim().min(1).max(254),
        password: z.string().min(1).max(1024),
    })
        .optional(),
});
/** The outcome of importing the directory. */
export const ldapSyncResultSchema = z.object({
    startedAt: isoTimestampSchema,
    finishedAt: isoTimestampSchema,
    ok: z.boolean(),
    /** Why the sync could not run, when `ok` is false. */
    error: z.string().nullable(),
    /** Directory entries that matched the user filter. */
    found: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    /** Existing local accounts attached to their directory entry. */
    linked: z.number().int().nonnegative(),
    /** Accounts whose directory entry is gone or no longer matches the filter. */
    disabled: z.number().int().nonnegative(),
    /** Accounts the directory had disabled and now lists again. */
    reenabled: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    /** Why entries were skipped - the first few. */
    notes: z.array(z.string()),
});
//# sourceMappingURL=ldap.js.map